import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { modalsMap, ModalName, ModalPropsMap } from '../utils/modalsMap';

type ModalState<N extends ModalName = ModalName> = {
    name: N;
    props: Omit<ModalPropsMap[N], 'open' | 'handleClose'>;
};

type ModalContextType = {
    openModal: <N extends ModalName>(name: N, props: Omit<ModalPropsMap[N], 'open' | 'handleClose'>) => void;
    closeModal: () => void;
};

export const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const ModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [modalStack, setModalStack] = useState<ModalState[]>([]);

    const openModal = useCallback(<N extends ModalName>(name: N, props: Omit<ModalPropsMap[N], 'open' | 'handleClose'>) => {
        setModalStack((prev) => [...prev, { name, props }]);
    }, []);

    const closeModal = useCallback(() => {
        setModalStack((prev) => prev.slice(0, -1));
    }, []);

    return (
        <ModalContext.Provider value={{ openModal, closeModal }}>
            {children}

            {modalStack.map(({ name, props }, index) => {
                const ModalComponent = modalsMap[name] as React.ComponentType<any>;
                const isTopModal = index === modalStack.length - 1;

                const key = `${name}-${JSON.stringify(props)}`;

                const modalProps = {
                    ...props,
                    open: true,
                    handleClose: isTopModal ? closeModal : () => {},
                };

                return <ModalComponent key={key} {...modalProps} />;
            })}
        </ModalContext.Provider>
    );
};
